import {
  tiktokSyncErrorCodeSchema,
  validationMessageKeySchema,
  type AdminUserStatus,
  type AuditAction,
  type AuditResult,
  type InvitationStatus,
  type PlatformRole,
  type TikTokAuthorizationCallbackErrorCode,
  type TikTokSyncErrorCode,
  type WorkspaceRoleSummary,
  type WorkspaceStatus,
} from "@metaadsmanager/tk-master-contracts";

import { ApiClientError } from "./api/client";
import {
  NetworkRequestError,
  RequestTimeoutError,
  classifyRequestFailure,
  failureDetailKey,
} from "./errors/request-failure";
import { createAppI18n, type AppI18n } from "../i18n/context";

type AuthErrorLike = { code?: unknown };
type Translate = AppI18n["t"];

const defaultTranslate = createAppI18n("zh-CN", () => undefined).t;

const authErrorKeys: Record<string, string> = {
  EMAIL_NOT_VERIFIED: "authErrors.EMAIL_NOT_VERIFIED",
  FAILED_TO_CREATE_SESSION: "authErrors.FAILED_TO_CREATE_SESSION",
  FAILED_TO_CREATE_USER: "authErrors.FAILED_TO_CREATE_USER",
  INVALID_EMAIL: "authErrors.INVALID_EMAIL",
  INVALID_EMAIL_OR_PASSWORD: "authErrors.INVALID_EMAIL_OR_PASSWORD",
  INVALID_PASSWORD: "authErrors.INVALID_PASSWORD",
  INVALID_TOKEN: "authErrors.INVALID_TOKEN",
  PASSWORD_TOO_LONG: "authErrors.PASSWORD_TOO_LONG",
  PASSWORD_TOO_SHORT: "authErrors.PASSWORD_TOO_SHORT",
  TOKEN_EXPIRED: "authErrors.TOKEN_EXPIRED",
  USER_ALREADY_EXISTS: "authErrors.USER_ALREADY_EXISTS",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL:
    "authErrors.USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL",
  REFERRAL_INVALID: "authErrors.REFERRAL_INVALID",
  REFERRAL_REVOKED: "authErrors.REFERRAL_REVOKED",
  REFERRAL_SERVICE_UNAVAILABLE: "authErrors.REFERRAL_SERVICE_UNAVAILABLE",
  REFERRAL_UNAVAILABLE: "authErrors.REFERRAL_UNAVAILABLE",
};

const tiktokErrorMessageKeys: Record<
  TikTokAuthorizationCallbackErrorCode,
  string
> = {
  tiktok_authorization_not_completed:
    "apiErrors.tiktok.authorization_not_completed",
  tiktok_permission_missing: "apiErrors.tiktok.permission_missing",
  tiktok_rate_limited: "apiErrors.tiktok.rate_limited",
  tiktok_response_invalid: "apiErrors.tiktok.response_invalid",
  tiktok_sync_timeout: "apiErrors.tiktok.sync_timeout",
  tiktok_token_invalid: "apiErrors.tiktok.reauthorization_required",
  tiktok_upstream_unavailable: "apiErrors.tiktok.upstream_unavailable",
};

const auditActionKeys: Record<AuditAction, string> = {
  "workspace.created": "labels.auditAction.workspace_created",
  "workspace.invitation.created": "labels.auditAction.workspace_invitation_created",
  "workspace.invitation.regenerated": "labels.auditAction.workspace_invitation_regenerated",
  "workspace.invitation.revealed": "labels.auditAction.workspace_invitation_revealed",
  "workspace.invitation.revoked": "labels.auditAction.workspace_invitation_revoked",
  "workspace.member.joined": "labels.auditAction.workspace_member_joined",
  "workspace.member.removed": "labels.auditAction.workspace_member_removed",
  "workspace.role.created": "labels.auditAction.workspace_role_created",
  "workspace.role.updated": "labels.auditAction.workspace_role_updated",
  "workspace.role.deleted": "labels.auditAction.workspace_role_deleted",
  "workspace.role.enabled": "labels.auditAction.workspace_role_enabled",
  "workspace.role.disabled": "labels.auditAction.workspace_role_disabled",
  "workspace.member.role_changed": "labels.auditAction.workspace_member_role_changed",
  "workspace.member.permission_granted": "labels.auditAction.workspace_member_permission_granted",
  "workspace.member.permission_revoked": "labels.auditAction.workspace_member_permission_revoked",
  "workspace.paused": "labels.auditAction.workspace_paused",
  "workspace.resumed": "labels.auditAction.workspace_resumed",
  "workspace.tiktok.authorized": "labels.auditAction.workspace_tiktok_authorized",
  "workspace.tiktok.business_centers_synced":
    "labels.auditAction.workspace_tiktok_business_centers_synced",
  "workspace.tiktok.advertiser_accounts_synced":
    "labels.auditAction.workspace_tiktok_advertiser_accounts_synced",
  "workspace.tiktok.stores_synced":
    "labels.auditAction.workspace_tiktok_stores_synced",
  "workspace.tiktok.creative_assets_synced":
    "labels.auditAction.workspace_tiktok_creative_assets_synced",
  "workspace.tiktok.campaigns_synced":
    "labels.auditAction.workspace_tiktok_campaigns_synced",
  "workspace.tiktok.campaign_reports_synced":
    "labels.auditAction.workspace_tiktok_campaign_reports_synced",
};

const tiktokSyncAuditActions = new Set<AuditAction>([
  "workspace.tiktok.business_centers_synced",
  "workspace.tiktok.advertiser_accounts_synced",
  "workspace.tiktok.stores_synced",
  "workspace.tiktok.creative_assets_synced",
  "workspace.tiktok.campaigns_synced",
  "workspace.tiktok.campaign_reports_synced",
]);

/** 把 Better Auth 稳定错误码转换为当前界面语言。 */
export function getLocalizedAuthErrorMessage(
  error: unknown,
  fallback = defaultTranslate("apiErrors.service.unavailable"),
  t: Translate = defaultTranslate,
): string {
  const code =
    typeof error === "object" && error !== null
      ? (error as AuthErrorLike).code
      : null;
  const key = typeof code === "string" ? authErrorKeys[code] : undefined;
  return key ? t(key) : fallback;
}

/** Web 只信任 API 稳定消息键，不直接展示服务端自由文本。 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  t: Translate = defaultTranslate,
): string {
  if (error instanceof NetworkRequestError) {
    return t("errors.networkDetail");
  }

  if (error instanceof RequestTimeoutError) {
    return t("apiErrors.tiktok.sync_timeout");
  }

  if (!(error instanceof ApiClientError)) {
    const classification = classifyRequestFailure(error);
    if (classification.kind === "network") {
      return t(failureDetailKey("network"));
    }
    return fallback;
  }
  const details = error.payload.error;
  const message = t(`apiErrors.${details.messageKey}`);
  const isManualRateLimit =
    details.messageKey === "tiktok.manual_sync_cooldown" ||
    details.messageKey === "tiktok.manual_sync_capacity";
  const retryAfterSeconds = details.retryAfterSeconds;
  if (
    !isManualRateLimit ||
    retryAfterSeconds === undefined ||
    !Number.isSafeInteger(retryAfterSeconds) ||
    retryAfterSeconds <= 0 ||
    retryAfterSeconds > 86_400
  ) {
    return message;
  }
  const retryAfter = retryAfterSeconds < 60
    ? t("tiktokResources.retryAfterSeconds", { seconds: retryAfterSeconds })
    : t("tiktokResources.retryAfterMinutes", {
        minutes: Math.ceil(retryAfterSeconds / 60),
      });
  return t("tiktokResources.retryWithDetail", { message, retryAfter });
}

export function getApiErrorRequestId(error: unknown): string | null {
  if (error instanceof NetworkRequestError || error instanceof RequestTimeoutError) {
    return error.requestId;
  }
  return error instanceof ApiClientError ? error.payload.requestId : null;
}

/** 稳定诊断字段：供排查定位，不含服务端自由文本。 */
export type ApiErrorDiagnostics = {
  code: string | null;
  messageKey: string | null;
  requestId: string | null;
  status: number | null;
};

export function getApiErrorDiagnostics(error: unknown): ApiErrorDiagnostics {
  if (error instanceof NetworkRequestError || error instanceof RequestTimeoutError) {
    return {
      code: error.name,
      messageKey: null,
      requestId: error.requestId,
      status: null,
    };
  }
  if (error instanceof ApiClientError) {
    return {
      code: error.payload.error.code,
      messageKey: error.payload.error.messageKey,
      requestId: error.payload.requestId,
      status: error.status,
    };
  }
  return {
    code: null,
    messageKey: null,
    requestId: null,
    status: null,
  };
}

/** 把 OAuth 与持久化同步分类映射为同一套可操作提示。 */
export function getTikTokErrorMessage(
  code: TikTokAuthorizationCallbackErrorCode | TikTokSyncErrorCode,
  t: Translate = defaultTranslate,
): string {
  return t(tiktokErrorMessageKeys[code]);
}

/** 将共享 Zod 稳定消息键翻译为当前界面语言。 */
export function getValidationMessage(
  message: string | undefined,
  t: Translate = defaultTranslate,
): string | undefined {
  const parsed = validationMessageKeySchema.safeParse(message);
  return parsed.success ? t(parsed.data) : message;
}

export const workspaceRoleLabel = (
  value: WorkspaceRoleSummary,
  t: Translate = defaultTranslate,
) => value.kind === "custom" ? value.name : t(`labels.workspaceRole.${value.kind}`);

export const workspaceStatusLabel = (
  value: WorkspaceStatus,
  t: Translate = defaultTranslate,
) => t(`labels.workspaceStatus.${value}`);

export const adminUserStatusLabel = (
  value: AdminUserStatus,
  t: Translate = defaultTranslate,
) => t(`labels.adminUserStatus.${value}`);

export const platformRoleLabel = (
  value: PlatformRole | null,
  t: Translate = defaultTranslate,
) => t(value ? `labels.platformRole.${value}` : "labels.platformRole.tenant");

export const invitationStatusLabel = (
  value: InvitationStatus,
  t: Translate = defaultTranslate,
) => t(`labels.invitationStatus.${value}`);

export const auditActionLabel = (
  value: AuditAction,
  t: Translate = defaultTranslate,
) => t(auditActionKeys[value]);

/** TikTok 审计失败只展示稳定错误码对应文案，既有业务原因保持原样。 */
export const auditReasonLabel = (
  action: AuditAction,
  reason: string | null,
  t: Translate = defaultTranslate,
): string | null => {
  if (reason === null) return null;
  if (!tiktokSyncAuditActions.has(action)) return reason;

  const parsed = tiktokSyncErrorCodeSchema.safeParse(reason);
  return parsed.success
    ? getTikTokErrorMessage(parsed.data, t)
    : t("common.notAvailable");
};

export const auditResultLabel = (
  value: AuditResult,
  t: Translate = defaultTranslate,
) => t(`labels.auditResult.${value}`);
